import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchClientById } from "@/lib/projects-clients";
import { Button } from "@/components/ui/button";
import { WorkScopeLoadingScreen } from "@/components/shared/WorkScopeLoadingScreen";
import { ArrowLeft } from "lucide-react";

type ClientDetailResponse = {
  id: string;
  name: string;
  companyName?: string | null;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  projects: Array<{ id: string; name: string; isActive: boolean }>;
  tickets: Array<{
    id: string;
    ticketNumber: string;
    projectId: string;
    type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK";
    status: string;
    totalAmount: string;
    createdAt: string;
    deletedAt?: string | null;
    deleteReason?: string | null;
    project?: {
      id: string;
      name: string;
      archivedAt?: string | null;
    } | null;
  }>;
  paymentSummary?: {
    approvedTotal: number;
    paidTotal: number;
    outstandingBalance: number;
  };
  payments?: Array<{
    id: string;
    amount: number;
    paymentDate: string;
    method: "CASH" | "CHECK" | "CARD" | "ZELLE" | "ACH" | "OTHER";
    note?: string | null;
    project?: {
      id: string;
      name: string;
    } | null;
  }>;
};

const ticketTypeLabel = (
  type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK",
  t: (key: string) => string,
) => {
  if (type === "TM_TICKET") return t("ticketTypes.estimate");
  if (type === "CHANGE_ORDER") return t("ticketTypes.changeOrder");
  return t("ticketTypes.extraWork");
};

const paymentMethodLabel = (
  method: "CASH" | "CHECK" | "CARD" | "ZELLE" | "ACH" | "OTHER",
  t: (key: string) => string,
) => {
  switch (method) {
    case "CASH":
      return t("paymentMethods.cash");
    case "CHECK":
      return t("paymentMethods.check");
    case "CARD":
      return t("paymentMethods.card");
    case "OTHER":
      return t("paymentMethods.other");
    default:
      return method;
  }
};

const ticketStatusClass = (status: string) => {
  switch (status) {
    case "APPROVED":
      return "bg-status-approved-bg text-status-approved";
    case "REJECTED":
      return "bg-destructive/10 text-destructive";
    case "NEEDS_CLARIFICATION":
      return "bg-primary/10 text-primary";
    case "PENDING_APPROVAL":
      return "bg-warning/20 text-warning";
    case "DRAFT":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

export default function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<ClientDetailResponse | null>(null);
  const projectNameById = new Map((client?.projects ?? []).map((project) => [project.id, project.name]));

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchClientById(id);
        setClient(data as ClientDetailResponse);
      } catch (e) {
        setError(e instanceof Error ? e.message : t("clientDetail.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id, t]);

  if (loading) return <WorkScopeLoadingScreen />;
  if (error || !client) return <div className="text-sm text-destructive">{error ?? t("clientDetail.notFound")}</div>;
  const paymentSummary = client.paymentSummary ?? { approvedTotal: 0, paidTotal: 0, outstandingBalance: 0 };
  const payments = client.payments ?? [];
  const typeLabel =
    client.type === "General Contractor"
      ? t("clients.types.generalContractor")
      : client.type === "Owner"
        ? t("clients.types.owner")
        : client.type === "Property Manager"
          ? t("clients.types.propertyManager")
          : client.type === "Developer"
            ? t("clients.types.developer")
            : client.type === "Other"
              ? t("clients.types.other")
              : client.type;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate("/clients")} className="p-2 rounded-md hover:bg-secondary">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[#0C1F4F]">{client.name}</h1>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold text-[#0C1F4F]">{t("clientDetail.primaryApprovalContact")}</h2>
          <Button variant="outline" size="sm" onClick={() => navigate(`/clients?edit=${client.id}`)}>
            {t("clients.editClient")}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <p><span className="text-muted-foreground">{t("clients.companyName")}:</span> {client.companyName ?? t("clientDetail.noCompany")}</p>
          <p><span className="text-muted-foreground">{t("common.email")}:</span> {client.email ?? t("common.noEmail")}</p>
          <p><span className="text-muted-foreground">{t("common.phone")}:</span> {client.phone ?? t("clientDetail.noPhone")}</p>
          <p><span className="text-muted-foreground">{t("clients.type")}:</span> {typeLabel ?? t("clientDetail.noType")}</p>
          <p><span className="text-muted-foreground">{t("clients.address")}:</span> {client.address ?? t("clientDetail.noAddress")}</p>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-3">
        <h2 className="font-semibold text-[#0C1F4F]">{t("nav.projects")}</h2>
        {client.projects.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("clientDetail.noProjectsLinked")}</p>
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate(`/projects?new=1&clientId=${client.id}`)}
            >
              {t("clientDetail.createProjectForClient")}
            </Button>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {client.projects.map((project) => (
              <button
                key={project.id}
                className="w-full rounded-md border border-accent/30 p-3 text-left hover:bg-muted/30"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <p className="font-medium text-[#0C1F4F]">{project.name}</p>
                <p className="text-xs text-muted-foreground">{project.isActive ? t("clientDetail.active") : t("clientDetail.inactive")}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h2 className="font-semibold text-[#0C1F4F]">{t("clientDetail.recentTickets")}</h2>
        </div>
        {client.tickets.length === 0 ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-muted-foreground">{t("clientDetail.noTicketsYet")}</p>
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate(`/tickets/new?clientId=${client.id}`)}
            >
              {t("clientDetail.createTicket")}
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("clientDetail.ticketNumber")}</th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("clientDetail.ticketType")}</th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("common.project")}</th>
                    <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("team.status")}</th>
                    <th className="text-right px-4 py-3 text-[#0C1F4F]">{t("clientDetail.value")}</th>
                  </tr>
                </thead>
                <tbody>
                  {client.tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className={`border-b last:border-0 ${
                        ticket.deleteReason === "project_archived"
                          ? "bg-muted/10 text-muted-foreground"
                          : "cursor-pointer hover:bg-muted/30"
                      }`}
                      onClick={ticket.deleteReason === "project_archived" ? undefined : () => navigate(`/tickets/${ticket.id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{ticket.ticketNumber}</td>
                      <td className="px-4 py-3 font-semibold text-accent">{ticketTypeLabel(ticket.type, t)}</td>
                      <td className="px-4 py-3">{ticket.project?.name ?? projectNameById.get(ticket.projectId) ?? "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ticketStatusClass(ticket.status)}`}>
                          {t(`ticketStatus.${ticket.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">${Number(ticket.totalAmount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-4 md:hidden">
              {client.tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`w-full rounded-lg border bg-card p-4 text-left ${
                    ticket.deleteReason === "project_archived"
                      ? "opacity-80"
                      : "transition-colors hover:bg-muted/20"
                  }`}
                  onClick={ticket.deleteReason === "project_archived" ? undefined : () => navigate(`/tickets/${ticket.id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-accent">{ticketTypeLabel(ticket.type, t)}</p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {ticket.project?.name ?? projectNameById.get(ticket.projectId) ?? "-"}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{client.name}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ticketStatusClass(ticket.status)}`}>
                        {t(`ticketStatus.${ticket.status}`)}
                      </span>
                      <p className="text-sm font-semibold text-accent">${Number(ticket.totalAmount).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-3">
          <h2 className="font-semibold text-[#0C1F4F]">{t("clientDetail.paymentDetails")}</h2>
          <p className="text-sm">
            <span className="text-muted-foreground">{t("clientDetail.outstanding")}:</span>{" "}
            <span className="font-semibold">${paymentSummary.outstandingBalance.toFixed(2)}</span>
          </p>
        </div>
        <div className="grid gap-2 border-b bg-accent/12 px-5 py-3 text-sm sm:grid-cols-3">
          <p><span className="text-muted-foreground">{t("clientDetail.approvedTotal")}:</span> ${paymentSummary.approvedTotal.toFixed(2)}</p>
          <p><span className="text-muted-foreground">{t("clientDetail.paidTotal")}:</span> ${paymentSummary.paidTotal.toFixed(2)}</p>
          <p><span className="text-muted-foreground">{t("clientDetail.balance")}:</span> ${paymentSummary.outstandingBalance.toFixed(2)}</p>
        </div>
        {payments.length === 0 ? (
          <div className="p-5">
            <p className="text-sm text-muted-foreground">{t("clientDetail.noRecordedPayments")}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("clientDetail.date")}</th>
                <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("clientDetail.method")}</th>
                <th className="text-left px-4 py-3 text-[#0C1F4F]">{t("common.project")}</th>
                <th className="text-right px-4 py-3 text-[#0C1F4F]">{t("clientDetail.amount")}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-b last:border-0">
                  <td className="px-4 py-3">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{paymentMethodLabel(payment.method, t)}</td>
                  <td className="px-4 py-3">{payment.project?.name ?? "-"}</td>
                  <td className="px-4 py-3 text-right">${Number(payment.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
